import { Box } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  FormButton,
  FormHeading,
  FormSubtitle,
} from '../components/Forms/Parts';
import { useGlobalLoader } from '../providers/globalLoader';
import { useTranslation } from '../providers/i18next';
import { logWarning } from '../providers/rollbar';
import PageBase from './PageLayout/PageBase';

const AUTO_RELOAD_KEY = 'StalledPageAutoReload';

/**
 * Whether this tab has already spent its one automatic reload for this path.
 * Defaults to `true` when sessionStorage is unavailable: if we cannot remember
 * having reloaded, we must not reload, or a broken tab would reload forever.
 */
const hasAutoReloaded = (path: string): boolean => {
  try {
    return window.sessionStorage.getItem(`${AUTO_RELOAD_KEY}:${path}`) === '1';
  } catch (err) {
    return true;
  }
};

const markAutoReloaded = (path: string): void => {
  try {
    window.sessionStorage.setItem(`${AUTO_RELOAD_KEY}:${path}`, '1');
  } catch (err) {
    // best-effort — hasAutoReloaded() already fails closed
  }
};

/**
 * Shown when auth initialization never settles and the user is left staring at
 * a spinner (MP-6664).
 *
 * A reload is the recovery that actually works for this class of hang: the
 * firebase user and the session id are both persisted by the time we get here,
 * so a fresh load settles quickly and continues on to the return page. That is
 * exactly what users have been doing by hand, so we do it for them once, and
 * then hand them a button if it did not take.
 *
 * Deliberately a reload and not a sign out: signing out discards a session that
 * is usually perfectly good and dumps the user back at the login page.
 */
const StalledPage = () => {
  const { txn } = useTranslation();
  const { disableLoader, setSkipLoadingTimeout } = useGlobalLoader();
  const { pathname } = useLocation();
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) {
      return;
    }
    didRun.current = true;

    disableLoader();
    setSkipLoadingTimeout();

    const autoReloadSpent = hasAutoReloaded(pathname);

    // Detection: this is the signal to alert on. A spike here means a flow is
    // hanging before it can reach its return page.
    logWarning(`Auth initialization stalled on ${pathname}`, {
      pathname,
      autoReloadSpent,
    });

    if (!autoReloadSpent) {
      markAutoReloaded(pathname);
      window.location.reload();
    }
  }, [disableLoader, pathname, setSkipLoadingTimeout]);

  return (
    <PageBase titleBase={txn('One moment')}>
      <FormHeading textAlign="center" title="">
        {txn('This is taking longer than expected')}
      </FormHeading>
      <FormSubtitle textAlign="center" title="">
        {txn('Reloading the page usually finishes signing you in.')}
      </FormSubtitle>
      <Box paddingTop={4}>
        <FormButton
          title={txn('Reload the page')}
          onClick={() => {
            window.location.reload();
          }}
        />
      </Box>
    </PageBase>
  );
};

export default StalledPage;
